import { useEffect, useState } from "react";
import { getSessionStorage, setSessionStorage } from "../utils/LocalStorage";
import { getLambdaRoute } from "../utils/getLambdaRoute";

// Wrap it all up in a hook for easy use.
/**
 * @param {string} email - The email to be used for enrichment.
 * @returns {{data: {}, done: boolean, errors: {}}} clearbitData - The clearbit response
 **/
export function useClearbitEnrichment(email) {
  // Abort if it dont make sense.
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  useEffect(() => {
    // console.log("useClearbitEnrichment useEffect")
    fetchClearbitData(email).then(res => {
      // console.log("useClearbitEnrichment useEffect res", res)
      setData(res.data);
      setErrors(res.errors);
    }).finally(() => {
      setDone(true);
    });
  }, [email]);
  return {
    data,
    errors,
    done
  };
}

// Actual call to clearbit api, shouldn't need to call this directly.
// Instead go through fetchClearbitData()
function queryClearbit(email) {
  // console.log("queryClearbit", email)
  return new Promise(resolve => {
    fetch(`${getLambdaRoute()}/clearbit-enrichment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({
        email: email
      })
    }).then(response => {
      if (response.ok) {
        response.json().then(data => {
          setSessionStorage("enrichment", JSON.stringify({
            data: data
          }));
          resolve({
            data: data,
            errors: {}
          });
        }).catch(e => {
          throw new Error("Bad JSON: " + e);
        });
      } else {
        throw new Error("Bad response from server: " + response.status);
      }
    }).catch(err => {
      const result = {
        data: {},
        errors: {}
      };
      result.errors[new Date().getTime()] = err;
      resolve({
        data: {},
        errors: result.errors
      });
    });
  });
}

// Check storage for a match, if not found, query clearbit.
// You can use this directly if a hook doesnt make sense,
// i.e fetching data as the result of a form submission.

export const fetchClearbitData = function (email) {
  return new Promise(resolve => {
    if (email) {
      try {
        let sessionData = getSessionStorage("enrichment");
        let hasEnrichment = sessionData ? JSON.parse(sessionData).data?.person?.email === email : false;
        if (hasEnrichment) {
          resolve({
            data: JSON.parse(sessionData),
            errors: {}
          });
        } else {
          queryClearbit(email).then(qbData => {
            resolve(qbData);
          });
        }
      } catch (e) {
        queryClearbit(email).then(qbData => {
          resolve(qbData);
        });
      }
    } else {
      resolve({
        data: {},
        errors: {
          email: "No email address supplied"
        }
      });
    }
  });
};
export const filterClearbitData = data => {
  if (!data?.company?.metrics?.employees || !data?.person?.email) {
    return data;
  }

  // Get the user email domain,
  // There are some email addreses that belong to franchises, or are generic
  // like @gmail.com.
  // Clearbit can sometimes figure out who a person is from a gmail address but
  // when it can't it sometimes sends back employee numbers for google llc
  // We are going to assume that there are not large firms that dont have their
  // own domain.

  const emaildomain = data.person.email.split("@")[1];
  // Ignore any number over 10 for these, its probably wrong
  const sub10Domains = ["gmail.com", "yahoo.com", "outlook.com", "mail.com"];

  // Ignore any number for these, we cant trust it from experience.
  const blacklistDomains = ["firstclassaccounts.com"];
  if (sub10Domains.includes(emaildomain) && data.company.metrics.employees > 10) {
    data.company.metrics.employees = undefined;
  }
  if (blacklistDomains.includes(emaildomain)) {
    data.company.metrics.employees = undefined;
  }
  return data;
};